import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  doc, 
  getDoc, 
  getDocs, 
  collection, 
  query, 
  where, 
  setDoc 
} from "firebase/firestore";
import { Typography } from "@mui/material";
import { db } from "../firebase";

import PrintableSinglesScoreSheet from "./PrintableSinglesScoreSheet";
import PrintableTeamsScoreSheet from "./PrintableTeamsScoreSheet";

function convertToISODate(dateStr) {
  const [dd, mm, yyyy] = dateStr.split("/");
  return `${yyyy}-${mm}-${dd}`;
}

function getWeekIndex(weekId) {
  if (!weekId) return 0;
  const match = weekId.match(/week-(\d+)/);
  return match ? Number(match[1]) : 0;
}

export default function PrintableScoreSheet() {
  const [searchParams] = useSearchParams();
  const [pennantData, setPennantData] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [fillIns, setFillIns] = useState({});
  const [teamsMapping, setTeamsMapping] = useState({});

  const [courtNumbers, setCourtNumbers] = useState({});

  const [mergedWeekData, setMergedWeekData] = useState(null);

  useEffect(() => {
    const pennantId = searchParams.get("pennantId");
    const weekDataParam = searchParams.get("weekData");
    if (!pennantId || !weekDataParam) return;

    const parsedWeekData = JSON.parse(weekDataParam);
    setWeekData(parsedWeekData);

    getDoc(doc(db, "Pennants", pennantId))
      .then((snap) => {
        if (snap.exists()) {
          setPennantData(snap.data());
        } else {
          console.error("No pennant doc found for:", pennantId);
        }
      })
      .catch((err) => console.error("Error loading pennant:", err));

    getDocs(collection(db, "Pennants", pennantId, "fillInRequired"))
      .then((snap) => {
        const fillInsMap = {};
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          fillInsMap[data.memberId] = data;
        });
        setFillIns(fillInsMap);
      })
      .catch((err) => console.error("Error loading fillIns:", err));

    const fetchTeams = async () => {
      try {
        const teamsSnap = await getDocs(collection(db, "Pennants", pennantId, "teams"));
        const teamsMap = {};
        teamsSnap.forEach((docSnap) => {
          teamsMap[docSnap.id] = docSnap.data();
        });
        setTeamsMapping(teamsMap);
      } catch (err) {
        console.error("Error fetching teams:", err);
      }
    };

    getDoc(doc(db, "Pennants", pennantId))
      .then((snap) => {
        if (snap.exists() && snap.data().playType === "Doubles") {
          fetchTeams();
        }
      });


    const fetchCourtNumbers = async (isoDate) => {
      try {
        const q = query(collection(db, "Bookings"), where("date", "==", isoDate));
        const bookingsSnap = await getDocs(q);
        const numbers = {};
        bookingsSnap.forEach((docSnap) => {
          const data = docSnap.data();

          if (data.drawMatchId) {
            numbers[data.drawMatchId] = data.courtNumber;
          }
        });
        setCourtNumbers(numbers);
      } catch (err) {
        console.error("Error fetching court numbers:", err);
      }
    };

    if (parsedWeekData.date) {
      const isoDate = convertToISODate(parsedWeekData.date);
      fetchCourtNumbers(isoDate);
    }
  }, [searchParams]);

  useEffect(() => {
    if (weekData && pennantData) {
      const pennantId = searchParams.get("pennantId");

      const weekIndex = getWeekIndex(weekData.id);

      const updatedMatches = (weekData.matches || []).map((match, mIndex) => {
        const drawMatchId = `pennant-${pennantId}-week-${weekIndex}-match-${mIndex}`;
        return {
          ...match,
          courtNumber: courtNumbers[drawMatchId] || match.courtNumber || "...",
          id: drawMatchId
        };
      });
      setMergedWeekData({
        ...weekData,
        matches: updatedMatches
      });
    }
  }, [weekData, pennantData, courtNumbers, searchParams]);

  if (!pennantData || !weekData || !mergedWeekData) {
    return <Typography>Loading Score Sheet...</Typography>;
  }

  const { participantType, playType } = pennantData;
  if (participantType === "Teams") {
    return (
      <PrintableTeamsScoreSheet
        pennantData={pennantData}
        weekData={mergedWeekData}
        fillIns={fillIns}
      />
    );
  }
  if (playType === "Doubles") {
    return (
      <PrintableSinglesScoreSheet
        pennantData={pennantData}
        weekData={mergedWeekData}
        fillIns={fillIns}
        playType="Doubles"
        teamsMapping={teamsMapping}
      />
    );
  }
  return (
    <PrintableSinglesScoreSheet
      pennantData={pennantData}
      weekData={mergedWeekData}
      fillIns={fillIns}
      playType="Singles"
    />
  );
}
